/**
 * Environment Configuration for CraftyJoy
 * 
 * This file manages environment-specific settings and ensures
 * demo users and test data are only available in development.
 */

// Environment detection
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;
export const appEnvironment = import.meta.env.VITE_APP_ENVIRONMENT || (isDevelopment ? 'development' : 'production');

// Demo mode is always enabled for all environments (for demo purposes)
export const demoConfig = {
  enableDemoUsers: true,
  showDemoSwitcher: true,
  enableSampleProjects: true,
  showTestCredentials: true,
  seedDemoData: true,
  productionDemoSafeguards: isProduction,
};

// Feature flags based on environment
export const featureFlags = {
  // AI Features
  enableAdvancedAI: import.meta.env.VITE_ENABLE_ADVANCED_AI === 'true',
  enableHuggingFace: import.meta.env.VITE_ENABLE_HUGGINGFACE === 'true',
  enableVoiceTraining: import.meta.env.VITE_ENABLE_VOICE_TRAINING === 'true',
  enableImageTraining: import.meta.env.VITE_ENABLE_IMAGE_TRAINING === 'true',
  enableSoundTraining: import.meta.env.VITE_ENABLE_SOUND_TRAINING === 'true',
  
  // Social Features
  enableCollaboration: import.meta.env.VITE_ENABLE_COLLABORATION === 'true',
  enableClassroomMode: import.meta.env.VITE_ENABLE_CLASSROOM_MODE === 'true',
  
  // Safety Features
  contentModerationEnabled: import.meta.env.VITE_CONTENT_MODERATION_ENABLED !== 'false',
  parentalControlsEnabled: import.meta.env.VITE_PARENTAL_CONTROLS_ENABLED !== 'false',
  coppaComplianceMode: import.meta.env.VITE_COPPA_COMPLIANCE_MODE !== 'false',
  
  // Development Features
  enableDebugMode: isDevelopment,
  enablePerformanceMonitoring: isProduction,
  enableErrorReporting: isProduction,
};

// API Configuration
export const apiConfig = {
  openaiApiKey: import.meta.env.VITE_OPENAI_API_KEY,
  huggingfaceApiKey: import.meta.env.VITE_HUGGINGFACE_API_KEY,
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
};

// Security settings
export const securityConfig = {
  // In production, require stronger authentication (except for demo users)
  requireEmailVerification: isProduction && !demoConfig.productionDemoSafeguards,
  
  // In production, enable stricter content filtering
  strictContentFiltering: isProduction,
  
  // In production, require HTTPS
  requireHttps: isProduction,
  
  // In production, enable CSRF protection
  enableCsrfProtection: isProduction,
  
  // Allow demo credentials in development or when demo override is enabled
  allowDemoCredentials: isDevelopment || demoConfig.productionDemoSafeguards,
};

// Console logging configuration
export const loggingConfig = {
  // Enable detailed console logs only in development
  enableDetailedLogs: isDevelopment,
  
  // Log demo user switches only in development
  logDemoActions: isDevelopment,
  
  // Log authentication actions (always enabled for security)
  logAuthActions: true,
  
  // Log errors (always enabled)
  logErrors: true,
  
  // Log performance metrics only in production
  logPerformance: isProduction,
};

// Validation functions
export const validateEnvironment = () => {
  const errors: string[] = [];
  
  // Check required environment variables
  if (isProduction) {
    if (!apiConfig.supabaseUrl) {
      errors.push('VITE_SUPABASE_URL is required in production');
    }
    if (!apiConfig.supabaseAnonKey) {
      errors.push('VITE_SUPABASE_ANON_KEY is required in production');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Helper functions
export const isDemoModeAllowed = (): boolean => {
  return demoConfig.enableDemoUsers;
};

export const shouldShowDemoSwitcher = (): boolean => {
  return demoConfig.showDemoSwitcher;
};

export const canUseDemoCredentials = (): boolean => {
  return securityConfig.allowDemoCredentials;
};

// Environment info for debugging (development only)
export const getEnvironmentInfo = () => {
  if (!isDevelopment) {
    return { environment: 'production', details: 'Environment details hidden in production' };
  }
  
  return {
    environment: appEnvironment,
    isDevelopment,
    isProduction,
    demoConfig,
    featureFlags,
    securityConfig,
    validation: validateEnvironment()
  };
};

// Production safety check
if (isProduction && demoConfig.enableDemoUsers && !demoConfig.productionDemoSafeguards) {
  console.error('🚨 SECURITY WARNING: Demo users are enabled in production! This should never happen.');
} else if (isProduction && demoConfig.productionDemoSafeguards) {
  console.warn('⚠️ Demo mode is enabled in production for demonstration purposes. Remember to disable after the demo.');
}

export default {
  isDevelopment,
  isProduction,
  appEnvironment,
  demoConfig,
  featureFlags,
  apiConfig,
  securityConfig,
  loggingConfig,
  validateEnvironment,
  isDemoModeAllowed,
  shouldShowDemoSwitcher,
  canUseDemoCredentials,
  getEnvironmentInfo
}; 